import { isFunction, merge } from 'lodash';
import { mergeObjects } from '../../object';
import { PAGE_NAME } from '../../../server/constants';
import { DEFAULT_REPORTED_CHANNELS } from './constants';

class EventsReporter {
    /**
     * Enrich all reported events with cross concern parameters.
     *
     * @param {Object} enrichmentParams - Enrichment params to be reported as part of every event.
     */
    enrich(enrichmentParams) {
        const baseEnrichmentParams = {
            pageName: PAGE_NAME,
        };

        this.enrichmentParams = mergeObjects(this.enrichmentParams, baseEnrichmentParams, enrichmentParams);
    }

    /**
     * Reports an event to all reporting channels (grafana, bigQuery, mixpanel).
     * In case no specific channel is supplied, the event is reported to all channels.
     *
     * @param {string} eventKey - The reported event key
     * @param {Object} eventParams - The reported event params
     * @param {Object[]} reportingChannels - The desired reporting channels for the event to report to
     */
    report(eventKey, eventParams = {}, reportingChannels = DEFAULT_REPORTED_CHANNELS) {
        if (!eventKey) {
            return;
        }

        reportingChannels.forEach(({ events, reporter }) => this.reportEvent(events[eventKey], reporter, eventParams));
    }

    /**
     * Reports an event to a specific reporting channel using a supplied reporter.
     *
     * @param {Object | Function} event - The reported event
     * @param {Function} reporter - The reporter method
     * @param {Object} eventParams - The event parameters
     */
    reportEvent(event, reporter, eventParams) {
        if (!event) {
            return;
        }

        const enrichedEvent = this.build(event, eventParams);

        reporter(enrichedEvent);
    }

    /**
     * Build an enriched event using the base enrichment parameters
     *
     * @param {Object | Function} event
     * @param {Object} eventParams
     * @returns {Object} The constructed event
     */
    build(event, eventParams = {}) {
        const enrichmentParams = merge({}, this.enrichmentParams, eventParams);

        return isFunction(event) ? event(enrichmentParams) : event;
    }
}

export default new EventsReporter();
